import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { 
  ArrowLeft, 
  Phone, 
  MessageCircle,
  Package,
  MapPin,
  Check,
  Clock,
  Bike,
  Navigation,
  Copy,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Loader2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { GoogleMap, useJsApiLoader, Marker, Polyline } from "@react-google-maps/api";
import axios from "axios";
import RatingModal from "../components/RatingModal";
import { getBackendUrl } from "../utils/api";

const API_URL = getBackendUrl();
const GOOGLE_MAPS_API_KEY = "AIzaSyCmX43q6uKe_2GmnVq8hQTi9WV4RrpSeRw";

const STATUS_STEPS = [
  { id: "rider_assigned", label: "Rider Assigned", icon: Bike },
  { id: "picked_up", label: "Package Picked Up", icon: Package },
  { id: "in_transit", label: "On the Way", icon: Navigation },
  { id: "delivered", label: "Delivered", icon: CheckCircle }
];

const FleetyGoTrackingPage = () => {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const location = useLocation();
  const [order, setOrder] = useState(null);
  const [copied, setCopied] = useState(false);
  const [riderPosition, setRiderPosition] = useState(null);
  const [showPinModal, setShowPinModal] = useState(false);
  const [enteredPin, setEnteredPin] = useState("");
  const [pinError, setPinError] = useState("");
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelResult, setCancelResult] = useState(null);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [hasRated, setHasRated] = useState(false);
  const [hasShownRatingModal, setHasShownRatingModal] = useState(() => {
    // Check localStorage to see if we've already shown the modal for this order
    const dismissedRatings = JSON.parse(localStorage.getItem("fleety_dismissed_ratings") || "[]");
    return dismissedRatings.includes(orderId);
  });

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: ["places"]
  });

  // Check if order has already been rated
  const checkRating = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/orders/${orderId}/has-rating`);
      setHasRated(response.data.has_rating);
      
      // If already rated, mark as shown in localStorage too
      if (response.data.has_rating) {
        const dismissedRatings = JSON.parse(localStorage.getItem("fleety_dismissed_ratings") || "[]");
        if (!dismissedRatings.includes(orderId)) {
          dismissedRatings.push(orderId);
          localStorage.setItem("fleety_dismissed_ratings", JSON.stringify(dismissedRatings));
        }
        return;
      }
      
      if (!hasShownRatingModal) {
        // Show rating modal after a small delay (only once ever per order)
        setHasShownRatingModal(true);
        // Save to localStorage so it doesn't show again
        const dismissedRatings = JSON.parse(localStorage.getItem("fleety_dismissed_ratings") || "[]");
        if (!dismissedRatings.includes(orderId)) {
          dismissedRatings.push(orderId);
          localStorage.setItem("fleety_dismissed_ratings", JSON.stringify(dismissedRatings));
        }
        setTimeout(() => setShowRatingModal(true), 1500);
      }
    } catch (e) {
      console.error("Error checking rating:", e);
    }
  }, [orderId, hasShownRatingModal]);

  const fetchOrder = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/fleetygo/orders/${orderId}`);
      setOrder(response.data);
      if (response.data.rider_lat && response.data.rider_lng) {
        setRiderPosition({ lat: response.data.rider_lat, lng: response.data.rider_lng });
      }
      
      // Check rating when order is delivered (only once ever per order)
      if (response.data.status === "delivered" && !hasRated && !hasShownRatingModal) {
        checkRating();
      }
    } catch (error) {
      console.error("Error fetching order:", error);
    }
  }, [orderId, hasRated, hasShownRatingModal, checkRating]);

  const handleCancelOrder = async () => {
    setIsCancelling(true);
    setCancelResult(null);
    try {
      const response = await axios.post(`${API_URL}/api/fleetygo/orders/${orderId}/cancel`);
      setCancelResult(response.data);
      if (response.data.success) {
        // Refresh order data
        fetchOrder();
      }
    } catch (error) {
      console.error("Error cancelling order:", error);
      setCancelResult({ 
        success: false, 
        message: error.response?.data?.detail || "Failed to cancel order. Please try again." 
      });
    }
    setIsCancelling(false);
  };

  // Check if order can be cancelled (only before trip starts - in_transit)
  const canCancel = order && ["pending", "payment_done", "rider_assigned", "picked_up"].includes(order.status);
  
  // Cancellation fee only applies after rider accepts (picked_up status)
  const hasCancellationFee = order?.status === "picked_up";
  const cancellationFee = hasCancellationFee ? 30 : 0;

  useEffect(() => {
    fetchOrder();
    
    // Poll for updates every 5 seconds
    const interval = setInterval(fetchOrder, 5000);
    return () => clearInterval(interval);
  }, [fetchOrder]);

  // Simulate rider movement
  useEffect(() => {
    if (!order || order.status === "delivered" || order.status === "pending") return;

    const simulateMovement = () => {
      if (!order) return;

      let startLat, startLng, endLat, endLng;

      if (order.status === "rider_assigned") {
        // Moving towards pickup
        startLat = order.sender_lat - 0.01;
        startLng = order.sender_lng - 0.01;
        endLat = order.sender_lat;
        endLng = order.sender_lng;
      } else if (order.status === "picked_up" || order.status === "in_transit") {
        // Moving towards delivery
        startLat = order.sender_lat;
        startLng = order.sender_lng;
        endLat = order.receiver_lat;
        endLng = order.receiver_lng;
      } else {
        return;
      }

      let progress = 0;
      const moveInterval = setInterval(() => {
        progress += 0.05;
        if (progress >= 1) {
          clearInterval(moveInterval);
          return;
        }

        const newLat = startLat + (endLat - startLat) * progress;
        const newLng = startLng + (endLng - startLng) * progress;
        
        setRiderPosition({ lat: newLat, lng: newLng });
        
        // Update rider location in backend
        axios.post(`${API_URL}/api/fleetygo/orders/${orderId}/update-rider-location`, null, {
          params: { lat: newLat, lng: newLng }
        }).catch(console.error);
      }, 2000);

      return () => clearInterval(moveInterval);
    };

    const cleanup = simulateMovement();
    return cleanup;
  }, [order?.status, orderId]);

  const copyPin = (pin) => {
    navigator.clipboard.writeText(pin);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const verifyPin = async () => {
    setPinError("");
    try {
      const pinType = order.status === "rider_assigned" ? "pickup" : "delivery";
      const response = await axios.post(
        `${API_URL}/api/fleetygo/orders/${orderId}/verify-pin`,
        null,
        { params: { pin: enteredPin, pin_type: pinType } }
      );
      
      if (response.data.success) {
        setShowPinModal(false);
        setEnteredPin("");
        fetchOrder();
      } else {
        setPinError(response.data.message);
      }
    } catch (error) {
      setPinError("Failed to verify PIN");
    }
  };

  const getStatusIndex = () => {
    return STATUS_STEPS.findIndex(s => s.id === order?.status) || 0;
  };

  if (!order) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#3FA9F5] border-t-transparent rounded-full" />
      </div>
    );
  }

  const mapCenter = riderPosition || { lat: order.sender_lat, lng: order.sender_lng };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="fleetygo-tracking">
      {/* Header */}
      <header className="bg-[#3FA9F5] p-4 flex items-center gap-4 sticky top-0 z-10">
        <button onClick={() => navigate("/fleetygo")} className="text-white">
          <ArrowLeft size={24} />
        </button>
        <div className="flex-1">
          <h1 className="text-white text-lg font-semibold">Track Delivery</h1>
          <p className="text-white/70 text-xs">Order #{order?.order_number || "N/A"}</p>
        </div>
      </header>

      {/* Map */}
      <div className="h-48 bg-gray-200 relative">
        {isLoaded && (
          <GoogleMap
            mapContainerStyle={{ width: "100%", height: "100%" }}
            center={mapCenter}
            zoom={14}
            options={{
              disableDefaultUI: true,
              zoomControl: true
            }}
          >
            {/* Pickup Marker */}
            <Marker
              position={{ lat: order.sender_lat, lng: order.sender_lng }}
              icon={{
                url: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%2322c55e' width='32' height='32'%3E%3Ccircle cx='12' cy='12' r='10'/%3E%3C/svg%3E",
                scaledSize: new window.google.maps.Size(20, 20)
              }}
            />
            
            {/* Drop Marker */}
            <Marker
              position={{ lat: order.receiver_lat, lng: order.receiver_lng }}
              icon={{
                url: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23ef4444' width='32' height='32'%3E%3Ccircle cx='12' cy='12' r='10'/%3E%3C/svg%3E",
                scaledSize: new window.google.maps.Size(20, 20)
              }}
            />

            {/* Rider Marker */}
            {riderPosition && (
              <Marker
                position={riderPosition}
                icon={{
                  url: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%233FA9F5' width='40' height='40'%3E%3Ccircle cx='12' cy='12' r='10' stroke='white' stroke-width='2'/%3E%3C/svg%3E",
                  scaledSize: new window.google.maps.Size(30, 30)
                }}
              />
            )}

            {/* Route Line */}
            <Polyline
              path={[
                { lat: order.sender_lat, lng: order.sender_lng },
                { lat: order.receiver_lat, lng: order.receiver_lng }
              ]}
              options={{
                strokeColor: "#3FA9F5",
                strokeWeight: 3,
                strokeOpacity: 0.7
              }}
            />
          </GoogleMap>
        )}
      </div>

      <main className="flex-1 p-4 overflow-y-auto">
        {/* Status Progress */}
        <div className="bg-white rounded-xl p-4 shadow-sm mb-4">
          <div className="flex items-center justify-between mb-4">
            {STATUS_STEPS.map((step, idx) => (
              <div key={step.id} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  idx <= getStatusIndex() ? "bg-[#3FA9F5]" : "bg-gray-200"
                }`}>
                  <step.icon size={16} className={idx <= getStatusIndex() ? "text-white" : "text-gray-400"} />
                </div>
                {idx < STATUS_STEPS.length - 1 && (
                  <div className={`w-8 h-1 mx-1 ${
                    idx < getStatusIndex() ? "bg-[#3FA9F5]" : "bg-gray-200"
                  }`} />
                )}
              </div>
            ))}
          </div>
          <p className="text-center font-semibold text-gray-900">
            {STATUS_STEPS.find(s => s.id === order.status)?.label || order.status}
          </p>
        </div>

        {/* Rider Info */}
        {order.rider_name && (
          <div className="bg-white rounded-xl p-4 shadow-sm mb-4">
            <h3 className="font-semibold text-gray-900 mb-3">Your Rider</h3>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#3FA9F5]/10 rounded-full flex items-center justify-center">
                  <Bike className="text-[#3FA9F5]" size={24} />
                </div>
                <div>
                  <p className="font-medium">{order.rider_name}</p>
                  <p className="text-sm text-gray-500">{order.rider_vehicle_number}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <a href={`tel:${order.rider_phone}`} className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <Phone size={18} className="text-green-600" />
                </a>
                <button className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <MessageCircle size={18} className="text-blue-600" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* PIN Section */}
        {order.status === "rider_assigned" && order.pickup_pin && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-4">
            <p className="text-sm text-yellow-800 font-medium mb-2">📍 Pickup PIN</p>
            <p className="text-xs text-yellow-600 mb-3">Share this PIN with the rider to start the delivery</p>
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-white rounded-lg p-3 text-center">
                <span className="text-2xl font-bold tracking-widest text-gray-900">{order.pickup_pin}</span>
              </div>
              <button
                onClick={() => copyPin(order.pickup_pin)}
                className="p-3 bg-white rounded-lg border"
              >
                {copied ? <Check size={20} className="text-green-500" /> : <Copy size={20} className="text-gray-500" />}
              </button>
            </div>
          </div>
        )}

        {/* Show delivery PIN ONLY when rider has reached delivery location */}
        {order.status === "in_transit" && order.rider_reached_delivery && order.delivery_pin && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4">
            <p className="text-sm text-green-800 font-medium mb-2">📦 Delivery PIN</p>
            <p className="text-xs text-green-600 mb-3">Share this PIN with the rider to confirm delivery</p>
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-white rounded-lg p-3 text-center">
                <span className="text-2xl font-bold tracking-widest text-gray-900">{order.delivery_pin}</span>
              </div>
              <button
                onClick={() => copyPin(order.delivery_pin)}
                className="p-3 bg-white rounded-lg border"
              >
                {copied ? <Check size={20} className="text-green-500" /> : <Copy size={20} className="text-gray-500" />}
              </button>
            </div>
          </div>
        )}

        {/* Show waiting message when in transit but rider hasn't reached yet */}
        {(order.status === "picked_up" || (order.status === "in_transit" && !order.rider_reached_delivery)) && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
            <p className="text-sm text-blue-800 font-medium mb-2">🚴 Rider is on the way</p>
            <p className="text-xs text-blue-600">Delivery PIN will be revealed when rider reaches your location</p>
          </div>
        )}

        {/* Delivery Complete */}
        {order.status === "delivered" && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4 text-center">
            <CheckCircle size={48} className="text-green-500 mx-auto mb-2" />
            <p className="text-lg font-semibold text-green-800">Delivery Complete!</p>
            <p className="text-sm text-green-600">Your package has been delivered successfully</p>
          </div>
        )}

        {/* Order Details */}
        <div className="bg-white rounded-xl p-4 shadow-sm mb-4">
          <h3 className="font-semibold text-gray-900 mb-3">Delivery Details</h3>
          
          {/* Pickup */}
          <div className="flex gap-3 mb-4">
            <div className="w-3 h-3 bg-green-500 rounded-full mt-1.5" />
            <div className="flex-1">
              <p className="text-xs text-gray-500">PICKUP</p>
              <p className="font-medium text-sm">{order.sender_name}</p>
              <p className="text-xs text-gray-600">{order.sender_address}</p>
            </div>
          </div>

          {/* Drop */}
          <div className="flex gap-3">
            <div className="w-3 h-3 bg-red-500 rounded-full mt-1.5" />
            <div className="flex-1">
              <p className="text-xs text-gray-500">DROP</p>
              <p className="font-medium text-sm">{order.receiver_name}</p>
              <p className="text-xs text-gray-600">{order.receiver_address}</p>
            </div>
          </div>
        </div>

        {/* Package Info */}
        <div className="bg-white rounded-xl p-4 shadow-sm mb-4">
          <h3 className="font-semibold text-gray-900 mb-2">Package</h3>
          <p className="text-sm text-gray-600">
            {order.package_contents} • {order.weight_category === "light" ? "< 5 kg" : order.weight_category === "medium" ? "5-10 kg" : "10-20 kg"}
          </p>
          {order.package_description && (
            <p className="text-xs text-gray-500 mt-1">{order.package_description}</p>
          )}
        </div>

        {/* Fare */}
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Total Paid</span>
            <span className="font-bold text-lg text-[#3FA9F5]">₹{order.total}</span>
          </div>
        </div>

        {/* Cancel Order Section - Show for orders before trip starts */}
        {canCancel && !cancelResult?.success && (
          <div className="mt-4">
            <button
              onClick={() => setShowCancelModal(true)}
              className="w-full py-3 px-4 bg-red-50 hover:bg-red-100 text-red-600 rounded-xl font-medium text-sm flex items-center justify-center gap-2 transition-colors border border-red-200"
            >
              <XCircle size={18} />
              Cancel Order & Get Refund
            </button>
            <p className="text-xs text-gray-500 text-center mt-2">
              {hasCancellationFee 
                ? "₹30 cancellation fee applies (rider has started)" 
                : "Full refund - No cancellation fee"}
            </p>
          </div>
        )}

        {/* Cancelled Order Info */}
        {order.status === "cancelled" && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <XCircle className="text-red-500 mt-0.5" size={20} />
              <div>
                <h3 className="font-semibold text-red-800">Order Cancelled</h3>
                {order.refund_amount > 0 && (
                  <p className="text-sm text-red-600 mt-1">
                    Refund of ₹{order.refund_amount} will be processed within 5-7 business days
                  </p>
                )}
                {order.cancellation_fee > 0 && (
                  <p className="text-xs text-red-500 mt-1">
                    Cancellation fee: ₹{order.cancellation_fee}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Refund Success Message */}
        {cancelResult?.success && (
          <div className="mt-4 bg-green-50 border border-green-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <CheckCircle className="text-green-500 mt-0.5" size={20} />
              <div>
                <h3 className="font-semibold text-green-800">Order Cancelled Successfully</h3>
                {cancelResult.refund_amount > 0 && (
                  <p className="text-sm text-green-600 mt-1">
                    Refund of ₹{cancelResult.refund_amount} will be credited within 5-7 business days
                  </p>
                )}
                {cancelResult.cancellation_fee > 0 && (
                  <p className="text-xs text-green-700 mt-1">
                    Cancellation fee deducted: ₹{cancelResult.cancellation_fee}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Cancel Confirmation Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="text-red-500" size={20} />
              </div>
              <h3 className="font-semibold text-lg">Cancel Order?</h3>
            </div>
            
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to cancel this delivery?
            </p>
            
            <div className="bg-gray-50 rounded-lg p-3 mb-4">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Order Total</span>
                <span className="font-medium">₹{order.total}</span>
              </div>
              {hasCancellationFee && (
                <div className="flex justify-between text-sm mt-1">
                  <span className="text-gray-600">Cancellation Fee (to rider)</span>
                  <span className="font-medium text-red-600">-₹{cancellationFee}</span>
                </div>
              )}
              <div className="flex justify-between text-sm mt-1 pt-2 border-t">
                <span className="font-medium">Refund Amount</span>
                <span className="font-bold text-green-600">
                  ₹{order.total - cancellationFee}
                </span>
              </div>
            </div>

            {cancelResult && !cancelResult.success && (
              <p className="text-red-500 text-sm mb-3">{cancelResult.message}</p>
            )}

            <div className="flex gap-2">
              <Button 
                onClick={() => setShowCancelModal(false)} 
                variant="outline" 
                className="flex-1"
                disabled={isCancelling}
              >
                Keep Order
              </Button>
              <Button 
                onClick={handleCancelOrder} 
                className="flex-1 bg-red-500 hover:bg-red-600"
                disabled={isCancelling}
              >
                {isCancelling ? (
                  <Loader2 className="animate-spin" size={18} />
                ) : (
                  "Cancel & Refund"
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* PIN Verification Modal */}
      {showPinModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-sm">
            <h3 className="font-semibold text-lg mb-4">Enter PIN</h3>
            <input
              type="text"
              value={enteredPin}
              onChange={(e) => setEnteredPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
              placeholder="4-digit PIN"
              className="w-full p-3 border rounded-lg text-center text-2xl tracking-widest mb-3"
              maxLength={4}
            />
            {pinError && <p className="text-red-500 text-sm mb-3">{pinError}</p>}
            <div className="flex gap-2">
              <Button onClick={() => setShowPinModal(false)} variant="outline" className="flex-1">
                Cancel
              </Button>
              <Button onClick={verifyPin} className="flex-1 bg-[#3FA9F5]">
                Verify
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Rating Modal - Shows automatically when package is delivered */}
      <RatingModal
        isOpen={showRatingModal}
        onClose={() => setShowRatingModal(false)}
        orderId={orderId}
        orderType="fleetygo"
        onRatingSubmitted={() => {
          setShowRatingModal(false);
          setHasRated(true);
        }}
      />
    </div>
  );
};

export default FleetyGoTrackingPage;
